import React, { FC, useMemo } from "react";
import clsx from "clsx";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import InputLabel from "@material-ui/core/InputLabel";
import { Theme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { KeyboardDatePicker as MuiDatePicker } from "@material-ui/pickers";
import { ReactComponent as DateRangeIcon } from "../../assets/svg/calendar.svg";
import InfoIcon from "../InfoIcon";
import { DATE_FORMATS } from "../../constants";

const useStyles = makeStyles((theme: Theme) => ({
  customContainer: {
    marginTop: 4,
    marginBottom: -18,
    paddingBottom: 21,
    "& > label": {
      top: 0,
      left: 0,
      color: "#a1a4ac",
      display: "block",
      position: "absolute",
      fontSize: 14,
      transform: "scale(1)",
      fontWeight: 400,
      "&.Mui-focused": {
        color: "#a1a4ac",
      },
    },
    "& .MuiIconButton-root": {
      color: "#bbbbbb",
    },
    "& .MuiInputAdornment-positionEnd > .MuiIconButton-root": {
      padding: "14px 12px 14px 12px",
    },
    "& .MuiInputBase-root": {
      minWidth: 160,
      lineHeight: "1 !important",
      fontSize: "16px !important",
      borderRadius: "15px !important",
      opacity: 1,
      width: "100% !important",
      height: "auto !important",
      position: "relative",
      border: "1px solid #ced4da",
      padding: "5px 16px 5px 16px",
      boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.04)",
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      boxSizing: "border-box",
      "&.Mui-focused": {
        borderColor: "#7d797a",
      },
      "&::after": {
        content: "none",
      },
      "&::before": {
        content: "",
        border: "none !important",
      },
      "& input::placeholder": {
        color: "#9195a4",
        opacity: 1,
      },
      "& input:-ms-input-placeholder": {
        color: "#9195a4",
        opacity: 1,
      },
      "& input::-ms-input-placeholder": {
        color: "#9195a4",
        opacity: 1,
      },
    },
    "& .Mui-error": {
      borderColor: theme.palette.error.main,
      "&.MuiInputBase-root.Mui-focused": {
        borderColor: `${theme.palette.error.main} !important`,
      },
    },
    "& .MuiFormHelperText-root": {
      display: "none",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      backgroundColor: "unset",
    },
    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "#bcbcbc33",
    },
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: "#a1a4ac",
    "& > span": {
      lineHeight: "inherit",
    },
  },
  helperText: {
    height: 0,
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
    color: theme.palette.error.main,
  },
  hasError: {
    color: theme.palette.error.main,
  },
}));

const DatePicker: FC<any> = ({
  input,
  dateFormat,
  label,
  infoIcon,
  required,
  helperText,
  meta: { dirtySinceLastSubmit, submitFailed, invalid, error, submitError },
  ...restProps
}) => {
  const classes = useStyles();

  const customOnBlur = (e) => {
    if (input.value) {
      e.target.parentElement.id = "MuiFocused";
    }
  };

  const hasError = useMemo(
    () => invalid && submitFailed && !dirtySinceLastSubmit,
    [invalid, dirtySinceLastSubmit, submitFailed],
  );

  return (
    <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
      <div>
        {!!label && (
          <InputLabel className={classes.label}>
            {label}{" "}
            {required && (
              <Typography
                className={clsx(classes.label, hasError && classes.hasError)}
                component={"span"}
                variant={"subtitle2"}
              >
                *
              </Typography>
            )}
            {infoIcon && <InfoIcon text={infoIcon} />}
          </InputLabel>
        )}
        <MuiDatePicker
          error={hasError}
          InputProps={null}
          id="date-picker-dialog"
          format={dateFormat || DATE_FORMATS.backEnd}
          autoComplete={"off"}
          {...input}
          {...restProps}
          value={input.value ? input.value : null}
          className={classes.customContainer}
          onBlur={customOnBlur}
          keyboardIcon={<DateRangeIcon />}
        />
        {((hasError && (error || submitError)) || !!helperText) && (
          <InputLabel className={classes.helperText}>
            {(hasError && ((error === 1 ? "" : error) || submitError)) ||
              helperText}
          </InputLabel>
        )}
      </div>
    </MuiPickersUtilsProvider>
  );
};
export default React.memo(DatePicker);
